import {PrismicNextLink} from '@prismicio/next'
import {LinksColumnSliceLinksColumnV2} from 'prismicio-types'
import cn from 'classnames'
import {isFilled} from '@prismicio/client'
import {EVENTS, track} from 'src/common/helpers'
import {useLinkResolver} from 'prismicio'
import {motion} from 'framer-motion'

const LinksColumnV2 = ({
  slice,
  context: {shouldHideTitle = false, pageUid},
  componentIndex,
}: {
  slice: LinksColumnSliceLinksColumnV2
  context: {shouldHideTitle?: boolean; pageUid: string}
  componentIndex: number
}): JSX.Element => {
  const {linkResolver} = useLinkResolver()

  return (
    <div
      className="col-span-full flex w-full flex-col px-6 py-8 text-black max-lg:p-0 lg:w-[30%] lg:max-w-[360px] lg:gap-2 lg:gap-y-8 lg:border-r-[1px] lg:border-black/[10%] lg:pt-6"
      data-slice-variation={slice.variation}
    >
      {isFilled.keyText(slice.primary.column_title) && (
        <span
          className={cn(
            'lg:text-tg-body-l w-full pb-4 text-[12px] leading-[14px] max-lg:px-4 max-lg:font-semibold max-lg:uppercase max-lg:text-moss-grey-400 lg:pb-0 lg:font-normal',
            {
              'max-lg:hidden': shouldHideTitle,
            },
          )}
        >
          {slice.primary.column_title}
        </span>
      )}
      <ul className="grid w-full list-none grid-cols-1 gap-x-4 gap-y-3 max-lg:divide-y max-lg:divide-black/[10%] max-lg:pb-6 lg:gap-y-6">
        {slice.primary.links.map((link, index) => {
          if (!isFilled.link(link.link_url)) {
            return null
          }

          return (
            <motion.li
              key={link.link_label! + index}
              whileHover="hover"
              className={cn('group/item isolate grow basis-0 max-lg:px-4', {
                'max-lg:pt-3': index !== 0,
              })}
            >
              <PrismicNextLink
                href={linkResolver(link.link_url)}
                {...(link.link_url.link_type === 'Web' && {target: link.link_url.target || '_self'})}
                onClick={() => {
                  track({
                    event: EVENTS.CLICK,
                    dataType: `order-${componentIndex}`,
                    location: 'main-nav-drop-down',
                    mktgId: `category-${slice.primary.marketing_id}-${index}`,
                    pageName: pageUid,
                  })
                }}
                className="flex items-center gap-3 font-normal"
              >
                {isFilled.image(link.icon) && (
                  <div className="flex h-8 min-w-8 items-center justify-center rounded-[4px] border border-black/[10%] bg-moss-white transition-colors duration-300 lg:border-black/[10%] lg:group-hover/item:border-black/[10%] lg:group-hover/item:bg-green-neon">
                    <div
                      className="h-4 w-4 bg-moss-grey-500 lg:group-hover/item:bg-dark-green"
                      style={{
                        maskImage: `url(${link.icon.url})`,
                        maskSize: 'contain',
                        maskRepeat: 'no-repeat',
                        WebkitMaskImage: `url(${link.icon.url})`,
                        WebkitMaskSize: 'contain',
                        WebkitMaskRepeat: 'no-repeat',
                      }}
                    />
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  <span className="text-[13.5px] leading-[normal] text-black">
                    {link.link_label}
                    <motion.span
                      initial={{opacity: 0}}
                      variants={{
                        hover: {opacity: 1},
                      }}
                      transition={{
                        duration: 0.3,
                      }}
                      className="pl-2 text-[12px]"
                    >
                      →
                    </motion.span>
                  </span>
                  {isFilled.keyText(link.link_description) && (
                    <span className="text-[13.5px] leading-[normal] tracking-[-.13px] text-black/[70%]">
                      {link.link_description}
                    </span>
                  )}
                </div>
              </PrismicNextLink>
            </motion.li>
          )
        })}
      </ul>
    </div>
  )
}

export default LinksColumnV2
